import React, { useState } from 'react';
import { ACTIONS_BY_RESOURCE } from './sandboxConfig';
import { useFetch } from '../api';
import { executeAction } from './SandboxApi';

function ActionInputForm({ action, onSubmit, loading }) {
  const [values, setValues] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = {};
    action.inputFields.forEach((field) => {
      if (field.type === 'json') {
        try { body[field.key] = JSON.parse(values[field.key] || '[]'); } catch { body[field.key] = []; }
      } else if (field.type === 'number') {
        body[field.key] = Number(values[field.key] || 0);
      } else if (field.type === 'select') {
        body[field.key] = values[field.key] || field.options?.[0] || '';
      } else {
        body[field.key] = values[field.key] || '';
      }
    });
    onSubmit(body);
  };

  const renderFieldInput = (field) => {
    const onChange = (e) => setValues({ ...values, [field.key]: e.target.value });
    if (field.type === 'json') {
      return (
        <textarea
          className="input text-xs font-mono mt-1"
          rows={3}
          value={values[field.key] || ''}
          onChange={onChange}
          placeholder='[{"sku": "...", "quantity": 1}]'
        />
      );
    }
    if (field.type === 'select') {
      return (
        <select
          className="input text-xs mt-1"
          value={values[field.key] || field.options[0]}
          onChange={onChange}
        >
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type}
        className="input text-xs mt-1"
        value={values[field.key] || ''}
        onChange={onChange}
      />
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 mt-3 p-3 bg-surface-50 rounded-lg border border-surface-200">
      {action.inputFields.map((field) => (
        <div key={field.key}>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="text-xs text-surface-500 mb-0.5 block">
            {field.label}
            {renderFieldInput(field)}
          </label>
        </div>
      ))}
      <button type="submit" className="btn btn-primary text-xs self-start" disabled={loading}>
        {loading ? 'Submitting...' : `${action.method} ${action.label}`}
      </button>
    </form>
  );
}

function ActionBar({
  row, sandboxType, resourceKey, connectionId, onRefresh, onUpdateClick, onModalAction,
}) {
  const trackstar = useFetch();
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedAction, setExpandedAction] = useState(null);
  const [error, setError] = useState(null);

  const actions = ACTIONS_BY_RESOURCE[sandboxType]?.[resourceKey] || [];

  const handleAction = (action, body = null) => {
    if (action.key === 'update') {
      onUpdateClick(row);
      return;
    }
    if (action.opensModal) {
      onModalAction(action.opensModal, row);
      return;
    }
    if (action.needsInput && !body) {
      setExpandedAction(expandedAction === action.key ? null : action.key);
      return;
    }
    setActionLoading(action.key);
    setError(null);
    const actionBody = action.buildBody ? action.buildBody(row, body || {}) : body;
    executeAction(trackstar, connectionId, action.method, action.endpoint(row.id), actionBody)
      .then(() => {
        setActionLoading(null);
        setExpandedAction(null);
        onRefresh();
      })
      .catch((err) => {
        setError(String(err));
        setActionLoading(null);
      });
  };

  if (actions.length === 0) return null;

  return (
    <div className="border-b border-surface-100 px-5 py-3">
      <div className="text-[10px] font-medium text-surface-400 uppercase tracking-wider mb-2">Actions</div>
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => {
          const disabled = action.disabledWhen ? action.disabledWhen(row) : false;
          const method = action.method || (action.key === 'update' ? 'PUT' : 'GET');
          return (
            <button
              key={action.key}
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-surface-200 bg-surface-0 hover:bg-surface-50 text-surface-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={disabled || actionLoading === action.key}
              onClick={() => handleAction(action)}
              title={action.endpoint ? `${method} /${action.endpoint(row.id)}` : undefined}
            >
              <span className={`request-${method} !text-[10px] !px-1 !py-0 !rounded`}>{method}</span>
              {actionLoading === action.key ? 'Loading...' : action.label}
            </button>
          );
        })}
      </div>
      {expandedAction && (() => {
        const action = actions.find((a) => a.key === expandedAction);
        if (!action) return null;
        return (
          <ActionInputForm
            action={action}
            loading={actionLoading === action.key}
            onSubmit={(body) => handleAction(action, body)}
          />
        );
      })()}
      {error && (
        <div className="mt-2 text-xs text-error-dark bg-error-light rounded-md px-3 py-2 border border-red-200">
          {error}
        </div>
      )}
    </div>
  );
}

export default ActionBar;
